/**
 * deadlineProjection.js — Phase 5.5 primary-surface metrics.
 *
 * Produces deadline-hit and budget-overrun probability estimates from
 * an ETA / cost projection band. The user-facing output is an ABSTRACT
 * TIER ('very_high' ... 'very_low') rather than a raw percentage, for
 * two reasons:
 *
 *   1. Statistical honesty — at small sample sizes, claiming "73% vs
 *      82%" is false precision. The underlying σ estimate has its own
 *      large uncertainty. Abstract tiers match the true calibration.
 *
 *   2. Cognitive ergonomics — an SPL triaging 15 projects doesn't
 *      distinguish 73% from 78%. Five well-spaced buckets (20/40/60/80)
 *      are what humans actually use for risk triage.
 *
 * Primary output format (also returned alongside):
 *   "Due May 17 · Expected May 20 · 2 days late · high probability"
 *
 * All functions are pure. Probability math assumes approximate normality
 * of the underlying quantity (ETA weeks, cost dollars) with σ estimated
 * from the band half-width. This is the same assumption the band itself
 * makes — no additional statistical licence is being taken here.
 */

import { normalCdf, sigmaFromBand } from '../statistics';

/**
 * Tier labels and their probability floors.
 *
 * The 20/40/60/80 boundaries are the product's locked choice (see
 * discussion in the Phase 5.5 plan). Boundaries are inclusive from below:
 *   p ≥ 0.80 → very_high
 *   p ≥ 0.60 → high
 *   p ≥ 0.40 → fifty_fifty   (the "coin-flip — pay attention" band)
 *   p ≥ 0.20 → low
 *   p  < 0.20 → very_low
 */
export const PROBABILITY_TIERS = ['very_low', 'low', 'fifty_fifty', 'high', 'very_high'];

// Probability thresholds (inclusive lower bounds). See TIER_TOOLTIP below for
// human-readable explanation. Phase 11 slot 1 (Invariant #16): named so any
// future tier-threshold change is grep-greppable + has a single source of truth.
const TIER_PROBABILITY_VERY_HIGH = 0.80;
const TIER_PROBABILITY_HIGH = 0.60;
const TIER_PROBABILITY_FIFTY_FIFTY = 0.40;
const TIER_PROBABILITY_LOW = 0.20;

// One-sided z-score for 80% confidence — used by `sigmaFromBand` inverse to
// back-compute σ from a confidence-band p20/p80 pair. 1.282 = Φ⁻¹(0.9) =
// the z that puts 80% of the normal distribution below it.
const Z_SCORE_80_PERCENT_ONE_SIDED = 1.282;

export const TIER_LABELS = {
  very_high: 'Very high probability',
  high: 'High probability',
  fifty_fifty: 'Coin flip',
  low: 'Low probability',
  very_low: 'Very low probability',
  insufficient_data: 'Not enough data to project',
};

/**
 * Human-readable explanation for the ⓘ tooltip accompanying a tier.
 * Kept in one place so it stays consistent wherever a tier is shown.
 */
export const TIER_TOOLTIP = {
  deadline:
    'Likelihood the project delivers on or before its deadline, based on ' +
    'team throughput, current progress, and the historical variance of both. ' +
    'Tiers: very low (<20%), low (20–40%), coin-flip (40–60%), high (60–80%), very high (≥80%).',
  cost:
    'Likelihood the project stays at or under its budget, based on current ' +
    'cost burn-rate and projected hours remaining. Tiers: very low (<20%), ' +
    'low (20–40%), coin-flip (40–60%), high (60–80%), very high (≥80%).',
};

/**
 * Map a raw probability in [0, 1] to a 5-tier label.
 * @param {number|null} p
 * @returns {'very_low'|'low'|'fifty_fifty'|'high'|'very_high'|'insufficient_data'}
 */
export function probabilityToTier(p) {
  if (p == null || !Number.isFinite(p)) return 'insufficient_data';
  if (p >= TIER_PROBABILITY_VERY_HIGH) return 'very_high';
  if (p >= TIER_PROBABILITY_HIGH) return 'high';
  if (p >= TIER_PROBABILITY_FIFTY_FIFTY) return 'fifty_fifty';
  if (p >= TIER_PROBABILITY_LOW) return 'low';
  return 'very_low';
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Given an ETA projection (from projectETA) and a deadline Date, return
 * the probability of completing on or before the deadline, bucketed to a
 * tier, plus a primary-surface-friendly descriptor.
 *
 * Semantics:
 *   - etaProjection.point is weeks-remaining (from projectETA).
 *   - deadline is a calendar Date (job.end_date or job.sla_delivery_date).
 *   - If the deadline is in the past AND the ETA isn't zero, the project
 *     is overdue → tier = 'very_low', overdue = true, daysDelta is the
 *     expected lateness in days (using projectedCompletionDate math).
 *   - If etaProjection is insufficient_data, we pass through insufficient.
 *
 * @param {Projection} etaProjection
 * @param {Date|string|number|null} deadline
 * @param {object} config — needs PROJECTION_BAND_Z for σ back-computation
 * @param {Date|number} [now]
 * @returns {{
 *   probability: number|null,
 *   tier: string,
 *   projectedDate: Date|null,
 *   deadlineDate: Date|null,
 *   daysDelta: number|null,
 *   overdue: boolean,
 *   reason?: string
 * }}
 */
export function projectDeadlineHit(etaProjection, deadline, config, now = new Date()) {
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const deadlineMs = deadline == null
    ? null
    : deadline instanceof Date
      ? deadline.getTime()
      : new Date(deadline).getTime();
  const deadlineDate = Number.isFinite(deadlineMs) ? new Date(deadlineMs) : null;

  // Insufficient data in → insufficient data out
  if (!etaProjection || etaProjection.confidence === 'insufficient_data' || etaProjection.point == null) {
    return {
      probability: null,
      tier: 'insufficient_data',
      projectedDate: null,
      deadlineDate,
      daysDelta: null,
      overdue: false,
      reason: etaProjection?.reason || 'no ETA projection',
    };
  }

  // No deadline set → we can project a date but can't compute hit probability.
  const projectedMs = nowMs + etaProjection.point * MS_PER_WEEK;
  const projectedDate = Number.isFinite(projectedMs) ? new Date(projectedMs) : null;
  if (deadlineDate == null) {
    return {
      probability: null,
      tier: 'insufficient_data',
      projectedDate,
      deadlineDate: null,
      daysDelta: null,
      overdue: false,
      reason: 'no deadline set',
    };
  }

  // Already done: ETA point == 0 means project is complete. No further
  // deadline forecast needed — shipping is past, risk is zero.
  if (etaProjection.point === 0) {
    return {
      probability: 1,
      tier: 'very_high',
      projectedDate,
      deadlineDate,
      daysDelta: Math.round((deadlineMs - projectedMs) / MS_PER_DAY),
      overdue: false,
      reason: 'already complete',
    };
  }

  // Overdue: deadline in the past + project not complete (etaPoint > 0).
  if (deadlineMs < nowMs) {
    const daysDelta = Math.round((deadlineMs - projectedMs) / MS_PER_DAY);
    return {
      probability: 0,
      tier: 'very_low',
      projectedDate,
      deadlineDate,
      daysDelta,  // negative = late
      overdue: true,
      reason: 'deadline passed',
    };
  }

  // Degenerate: ETA is deterministic (exact single-value). We only take
  // this branch when BOTH bounds are non-null AND equal to point — the
  // null-null case falls through to the sigma-fallback path below, which
  // sets a reason explaining the single-sample limitation.
  const hasTightBand =
    etaProjection.low != null && etaProjection.high != null && etaProjection.low === etaProjection.high;
  if (etaProjection.method === 'deterministic' || hasTightBand) {
    const hit = projectedMs <= deadlineMs;
    const daysDelta = Math.round((deadlineMs - projectedMs) / MS_PER_DAY);
    return {
      probability: hit ? 1 : 0,
      tier: hit ? 'very_high' : 'very_low',
      projectedDate,
      deadlineDate,
      daysDelta,
      overdue: false,
    };
  }

  // Main case: approximate-normal ETA with σ back-computed from band.
  // R12 MED-1 audit fix: pass sampleSize so sigmaFromBand uses the same
  // Student-t-corrected critical value that built the band at small n.
  // Without this, reconstructed σ was inflated by crit/z (~1.47× at n=3),
  // biasing tier assignments toward "fifty_fifty" near the 40/60 boundary.
  const bandZ = config?.PROJECTION_BAND_Z ?? Z_SCORE_80_PERCENT_ONE_SIDED;
  const sigma = sigmaFromBand(etaProjection, bandZ, etaProjection.sampleSize);
  if (sigma == null) {
    // Band couldn't be formed (e.g. n=1 point-only). Fall back to deterministic.
    const hit = projectedMs <= deadlineMs;
    const daysDelta = Math.round((deadlineMs - projectedMs) / MS_PER_DAY);
    return {
      probability: hit ? 1 : 0,
      tier: hit ? 'very_high' : 'very_low',
      projectedDate,
      deadlineDate,
      daysDelta,
      overdue: false,
      reason: 'no variance (single-sample band)',
    };
  }

  // Available weeks from now to deadline.
  const availableWeeks = (deadlineMs - nowMs) / MS_PER_WEEK;
  // P(ETA ≤ availableWeeks) = Φ((availableWeeks − μ) / σ)
  const z = sigma > 0 ? (availableWeeks - etaProjection.point) / sigma : Infinity;
  const probability = normalCdf(z);
  const tier = probabilityToTier(probability);
  const daysDelta = Math.round((deadlineMs - projectedMs) / MS_PER_DAY);

  return {
    probability,
    tier,
    projectedDate,
    deadlineDate,
    daysDelta,
    overdue: false,
  };
}

/**
 * Cost-side mirror of projectDeadlineHit.
 *
 * Given a cost projection (cost of work still REMAINING) and a budget
 * (total cost ceiling) and `dollarsSpent` (cost already incurred on the
 * work that's DONE), returns the probability that the final total cost
 * (spent + remaining) lands at or below budget.
 *
 * R3 audit H2 fix: previously compared remaining-cost against total
 * budget, which produced nonsense like "cost $52K, very high probability
 * under $50K budget" when $48K was already spent. The correct comparison
 * is remaining-cost against remaining-budget = (budget − dollarsSpent).
 *
 * R3 audit M2 fix: null low/high now correctly falls through to the
 * sigma-null path (with reason), matching projectDeadlineHit's semantics.
 *
 * @param {Projection} costProjection — cost of REMAINING work
 * @param {number} budget — total project budget in USD
 * @param {number} dollarsSpent — already-spent cost on completed work
 * @param {object} config — needs PROJECTION_BAND_Z
 * @returns {{
 *   probability: number|null,
 *   tier: string,
 *   projectedCost: number|null,   // total projected (spent + remaining)
 *   remainingCost: number|null,   // just the remaining portion
 *   dollarsSpent: number,
 *   budget: number|null,
 *   remainingBudget: number|null, // budget - spent
 *   dollarsDelta: number|null,    // positive = under budget, negative = over
 *   reason?: string
 * }}
 */
export function projectCostOverrun(costProjection, budget, dollarsSpent, config) {
  const spent = Number.isFinite(dollarsSpent) && dollarsSpent >= 0 ? dollarsSpent : 0;

  if (!costProjection || costProjection.confidence === 'insufficient_data' || costProjection.point == null) {
    return {
      probability: null,
      tier: 'insufficient_data',
      projectedCost: null,
      remainingCost: null,
      dollarsSpent: spent,
      budget: Number.isFinite(budget) && budget > 0 ? budget : null,
      remainingBudget: null,
      dollarsDelta: null,
      reason: costProjection?.reason || 'no cost projection',
    };
  }
  if (!Number.isFinite(budget) || budget <= 0) {
    return {
      probability: null,
      tier: 'insufficient_data',
      projectedCost: spent + costProjection.point,
      remainingCost: costProjection.point,
      dollarsSpent: spent,
      budget: null,
      remainingBudget: null,
      dollarsDelta: null,
      reason: 'no budget set',
    };
  }

  const remainingBudget = budget - spent;
  const totalProjected = spent + costProjection.point;
  const dollarsDelta = budget - totalProjected;  // positive = under budget total

  // Deterministic / degenerate band → binary outcome.
  // R3 M2 audit fix: only enter this branch when BOTH bounds are non-null
  // AND equal to each other. The null-null case falls through to the
  // sigma-null path below so it sets a "no variance" reason.
  const hasTightBand =
    costProjection.low != null && costProjection.high != null && costProjection.low === costProjection.high;
  if (costProjection.method === 'deterministic' || hasTightBand) {
    const hit = totalProjected <= budget;
    return {
      probability: hit ? 1 : 0,
      tier: hit ? 'very_high' : 'very_low',
      projectedCost: totalProjected,
      remainingCost: costProjection.point,
      dollarsSpent: spent,
      budget,
      remainingBudget,
      dollarsDelta,
    };
  }

  // R12 MED-1 audit fix: pass sampleSize for Student-t-corrected σ (see
  // projectDeadlineHit for rationale).
  const bandZ = config?.PROJECTION_BAND_Z ?? Z_SCORE_80_PERCENT_ONE_SIDED;
  const sigma = sigmaFromBand(costProjection, bandZ, costProjection.sampleSize);
  if (sigma == null) {
    const hit = totalProjected <= budget;
    return {
      probability: hit ? 1 : 0,
      tier: hit ? 'very_high' : 'very_low',
      projectedCost: totalProjected,
      remainingCost: costProjection.point,
      dollarsSpent: spent,
      budget,
      remainingBudget,
      dollarsDelta,
      reason: 'no variance (single-sample band)',
    };
  }

  // P(remaining ≤ remainingBudget) = Φ((remainingBudget − point) / σ)
  // = P(total projected ≤ budget)
  const z = sigma > 0 ? (remainingBudget - costProjection.point) / sigma : Infinity;
  const probability = normalCdf(z);
  const tier = probabilityToTier(probability);

  return {
    probability,
    tier,
    projectedCost: totalProjected,
    remainingCost: costProjection.point,
    dollarsSpent: spent,
    budget,
    remainingBudget,
    dollarsDelta,
  };
}

/**
 * Human-readable descriptor of a deadline-hit result.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * R22 LOW-2 audit status: RESERVED — no current runtime importers.
 *
 * Phase 5.5's `DeadlineMetric.jsx` component renders its own inline
 * delta-text (`formatDate` + per-tier branch at the top of the component),
 * bypassing this helper. The prior docstring claim "Used by the primary
 * surface metric component" was drift (false at audit time).
 *
 * Preserved because:
 *   1. The 6 tests at `deadlineProjection.test.js` exercise canonical
 *      format cases and serve as a specification for the display format
 *      every deadline surface should converge on.
 *   2. A Phase 6 consolidation pass could refactor `DeadlineMetric.jsx` +
 *      any other inline deadline formatters to this helper for
 *      single-source-of-truth formatting. Same rationale as
 *      ConfidenceBand.jsx (R21 LOW-1 reservation).
 *
 * If consolidation doesn't materialize in Phase 6, delete this function
 * and its test block — git preserves both.
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   "Due May 17 · Expected May 20 · 2 days late"
 *   "Due May 17 · Expected May 14 · 3 days early"
 *   "Due May 17 · Expected May 17 · on time"
 *   "Overdue since May 17 · still 2 weeks of work · 14 days late"
 *   "Expected May 20 · no deadline set"
 *
 * @param {ReturnType<projectDeadlineHit>} hit
 * @returns {{line1: string, line2: string}} — two-line descriptor for UI
 */
export function formatDeadlineDescriptor(hit) {
  const fmt = (d) => d == null ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const due = hit?.deadlineDate ? `Due ${fmt(hit.deadlineDate)}` : 'No deadline';
  const expected = hit?.projectedDate ? `Expected ${fmt(hit.projectedDate)}` : 'Expected —';
  // R3 L1 audit fix: pluralize days (1 day late, 2 days late).
  const plural = (n) => n !== 1 ? 's' : '';
  let delta = '';
  if (hit?.daysDelta == null) {
    delta = '';
  } else if (hit.overdue) {
    const n = Math.abs(hit.daysDelta);
    delta = `${n} day${plural(n)} late`;
  } else if (hit.daysDelta === 0) {
    delta = 'on time';
  } else if (hit.daysDelta > 0) {
    delta = `${hit.daysDelta} day${plural(hit.daysDelta)} early`;
  } else {
    const n = Math.abs(hit.daysDelta);
    delta = `${n} day${plural(n)} late`;
  }
  return {
    line1: [due, expected].filter(Boolean).join(' · '),
    line2: delta,
  };
}
